import axios from 'axios';
import { createApiClient } from '../../api/client';
import type { CardDashboardResponse } from './types';

/**
 * card-dashboard talks to the `carddashboard` REST API
 * (`/api/v1/carddashboard/*`, which sits under the existing `custom-flows`
 * base). Contract source: `flows/card-dashboard/API_CONTRACT.md` (authoritative
 * — PRD §7).
 *
 * Auth: standard authenticated embed. The host-injected bearer JWT (carrying
 * the `CAN_SEE_CARD_DASHBOARD` authority) travels in the `Authorization`
 * header, attached by `createApiClient` — never in the body. The backend picks
 * the web vs mobile dataset from the token's `p` claim, so the FE sends no
 * `isMobile` flag.
 *
 * Base URL is `VITE_API_URL` from `.env.<mode>` — empty in dev / unit tests so
 * MSW (relative-path handlers) intercepts; per-env builds point it at the real
 * service.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';
export const FETCH_DASHBOARD_DATA_PATH = '/api/v1/carddashboard/fetch-dashboard-data';

const apiClient = () => createApiClient(API_BASE);

export type FetchDashboardArgs = {
  /** FE parity only — the embedding app's current route. Backend ignores it. */
  route?: string;
  /** Formatting locale; blank ⇒ backend falls back to `user.locale` then en-US. */
  language?: string;
};

/** Fetch the full dashboard payload (cards + grouped transactions + user). */
export const fetchDashboardData = async (
  args: FetchDashboardArgs = {},
): Promise<CardDashboardResponse> => {
  const { data } = await apiClient().post<CardDashboardResponse>(
    FETCH_DASHBOARD_DATA_PATH,
    args,
  );
  return data;
};

// --- Error extraction ------------------------------------------------------

export type ApiError = {
  /** HTTP status (undefined for a transport/network error with no response). */
  status?: number;
  /** RFC-7807 `errorCode` when present (e.g. `UNAUTHORIZED`, `FORBIDDEN`). */
  errorCode: string | null;
};

type ProblemDetail = { errorCode?: string };

/** Normalise an unknown thrown error into `{ status, errorCode }`. */
export const parseApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ProblemDetail | undefined;
    return { status: error.response?.status, errorCode: body?.errorCode ?? null };
  }
  return { errorCode: null };
};
